import { Banknote, LogOut, Monitor, RefreshCw } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { BrandLockup } from "@/components/BrandMark.tsx";
import { ApiError } from "@/lib/api.ts";
import { useCurrentCashSession, useOpenCashSession } from "@/queries/cashSessions.ts";
import { useAuthStore } from "@/stores/auth.ts";
import { useTerminalStore } from "@/stores/terminal.ts";

export function OpenCashPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const terminal = useTerminalStore((s) => s.terminal);
  const unpairTerminal = useTerminalStore((s) => s.unpair);

  const currentSession = useCurrentCashSession();
  const openSession = useOpenCashSession();
  const [openedAmount, setOpenedAmount] = useState("0.00");

  // Si ya hay sesión abierta, ir al POS.
  useEffect(() => {
    if (currentSession.data) {
      navigate("/", { replace: true });
    }
  }, [currentSession.data, navigate]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    openSession.mutate(
      { opened_amount: openedAmount },
      {
        onSuccess: () => navigate("/", { replace: true }),
      },
    );
  };

  const errorMsg =
    openSession.error instanceof ApiError ? openSession.error.message : openSession.error?.message;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="flex h-20 items-center justify-between border-b border-slate-200 bg-white px-6">
        <div className="flex items-center gap-4">
          <BrandLockup caption={`Cajero: ${user?.full_name ?? ""}`} />
          {terminal && (
            <button
              type="button"
              onClick={() => {
                unpairTerminal();
                navigate("/select-terminal", { replace: true });
              }}
              title={`Cambiar caja (actual: ${terminal.code})`}
              className="flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-800 hover:border-brand-300 hover:bg-brand-100"
            >
              <Monitor className="h-3.5 w-3.5" />
              <span className="font-mono">{terminal.code}</span>
              <RefreshCw className="h-3 w-3 opacity-60" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            clearSession();
            unpairTerminal();
            navigate("/login", { replace: true });
          }}
          className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <LogOut className="h-4 w-4" /> Salir
        </button>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-10">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md rounded-[var(--radius-card)] border border-slate-200 bg-white p-8"
        >
          <div className="mb-6 flex items-center gap-3">
            <div className="rounded-xl bg-brand-50 p-3 text-brand-700">
              <Banknote className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-slate-900">Iniciar turno</h2>
              <p className="text-sm text-slate-500">
                Cuenta el efectivo inicial en caja para arrancar.
              </p>
            </div>
          </div>

          <label className="mb-2 block text-sm font-medium text-slate-700">
            Monto inicial en efectivo (S/)
          </label>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={openedAmount}
            onChange={(e) => setOpenedAmount(e.target.value)}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-2xl font-bold tabular-nums text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/15"
            placeholder="0.00"
            autoFocus
          />

          {errorMsg && (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {errorMsg}
            </p>
          )}

          <button
            type="submit"
            disabled={openSession.isPending}
            className="mt-6 w-full rounded-xl bg-brand-600 py-4 text-base font-extrabold text-white hover:bg-brand-700 active:bg-brand-800 disabled:opacity-50"
          >
            {openSession.isPending ? "Abriendo..." : "Abrir caja"}
          </button>
        </form>
      </main>
    </div>
  );
}
